import axios from "axios";
import config from "../config/index.js";

/**
 * Pre-configured Axios instance pointed at the GILDer backend.
 * Adds a 15s timeout and JSON content type by default.
 */
const api = axios.create({
    baseURL: config.backendUrl,
    timeout: 15000,
    headers: {
        "Content-Type": "application/json",
        // Free-tier ngrok normally serves an HTML interstitial ("You are
        // about to visit ...") on the very first load, which then fails
        // every subsequent JSON parse and looks like a CORS / 502 error
        // in DevTools. This header is the documented bypass.
        "ngrok-skip-browser-warning": "true"
    }
});

/**
 * Lists all deposits owned by an address. Backend serves indexed events so
 * this avoids hammering the chain RPC for view calls.
 */
export async function fetchDepositsByOwner(owner) {
    const { data } = await api.get(`/api/deposits/by-owner/${owner}`);
    return data;
}

/**
 * Inserts a freshly opened deposit into the backend. Idempotent — if the
 * indexer (or an earlier call) already saved the same `depositId`, the
 * server returns the existing record with status "exists" instead of
 * creating a duplicate. Used as a fast-path so the new deposit appears in
 * the user's dashboard immediately, without waiting for the next indexer
 * tick.
 */
export async function upsertDeposit(payload) {
    const { data } = await api.post("/api/deposits", payload);
    return data;
}

/**
 * §V6.2 — forces the backend to re-read this deposit's mutable fields
 * (compoundMode / state / loanBalance / accruedInterest) straight from
 * the chain. Used immediately after a `setCompoundMode` tx confirms,
 * since older contract builds don't emit `CompoundModeSet` and so the
 * indexer can't observe the toggle change — without this call the row
 * would snap back to its old value on the next refresh.
 */
export async function resyncDeposit(depositId) {
    const { data } = await api.post(`/api/deposits/${depositId}/resync`);
    return data;
}

/**
 * §V6.2 — live dynamic-keeper-fee estimate. Backend re-runs the
 * on-chain `CYR.calculateKeeperFee(gasUsed, gasPrice)` against the
 * current ETH/USD feed and provider gas price. Polled every ~30s by
 * surfaces that quote the sweep fee to the user (Reinvest meter,
 * Deposit page Auto-Compound copy).
 */
export async function fetchKeeperFeeEstimate() {
    const { data } = await api.get("/api/reinvestment/keeper-fee-estimate");
    return data;
}

/**
 * Returns the wallet's transactions, paginated and newest first.
 * Optional filters: `type` (deposit_opened | deposit_settled | early_exit
 * | abandoned_fee | loan_opened | nft_transfer_in | nft_transfer_out)
 * and `depositId`. Used by Recent Transactions, the Transactions page,
 * and the Activity feed.
 */
export async function fetchTransactionsByOwner(owner, { limit, skip, type, depositId } = {}) {
    const params = new URLSearchParams();
    if (limit != null) params.set("limit", String(limit));
    if (skip != null) params.set("skip", String(skip));
    if (type) params.set("type", type);
    if (depositId != null) params.set("depositId", String(depositId));
    const qs = params.toString();
    const path = `/api/transactions/by-owner/${owner}${qs ? `?${qs}` : ""}`;
    const { data } = await api.get(path);
    return data;
}

/**
 * Server-computed portfolio-value-over-time series for the Dashboard's
 * Total Returns chart. Backend replays the wallet's deposit lifecycle
 * and returns samples + the peak point — frontend just renders.
 */
export async function fetchPortfolioHistory(owner, { windowId = "7d", samples = 61 } = {}) {
    const params = new URLSearchParams({ windowId, samples: String(samples) });
    const { data } = await api.get(`/api/transactions/portfolio-history/${owner}?${params}`);
    return data;
}

/**
 * Global recent transactions feed across every wallet. Useful for an
 * admin / activity dashboard once that surface is built.
 */
export async function fetchRecentTransactions({ limit, skip, type } = {}) {
    const params = new URLSearchParams();
    if (limit != null) params.set("limit", String(limit));
    if (skip != null) params.set("skip", String(skip));
    if (type) params.set("type", type);
    const qs = params.toString();
    const path = `/api/transactions${qs ? `?${qs}` : ""}`;
    const { data } = await api.get(path);
    return data;
}

/** Returns the latest treasury snapshot (totals, TVT, reserve buckets). */
export async function fetchTreasurySnapshot() {
    const { data } = await api.get("/api/treasury/snapshot");
    return data;
}

/** Returns the latest Treasury reserve buckets + outstanding loan principal. */
export async function fetchTreasuryReserves() {
    const { data } = await api.get("/api/treasury/reserves");
    return data;
}

/** Returns the live TVT reading (computed server-side from the TVT contract). */
export async function fetchTvtCurrent() {
    const { data } = await api.get("/api/tvt/current");
    return data;
}

/** Returns TVT history (snapshots over time). */
export async function fetchTvtHistory({ limit = 100 } = {}) {
    const { data } = await api.get(`/api/tvt/history?limit=${limit}`);
    return data;
}

/** Returns TVT history filtered + downsampled to a chart-ready window. */
export async function fetchTvtSeries({ windowId = "30d", samples = 61 } = {}) {
    const params = new URLSearchParams({ windowId, samples: String(samples) });
    const { data } = await api.get(`/api/tvt/series?${params}`);
    return data;
}

/**
 * Returns the current §F.4 Price-Band Zone (Discount / Neutral /
 * Premium) plus spot + intrinsic price + observation timestamp.
 */
export async function fetchTvtZone() {
    const { data } = await api.get("/api/tvt/zone");
    return data;
}

/** Backend-computed preview of an early-exit (penalty + payout split). */
export async function fetchExitPreview(depositId) {
    const { data } = await api.get(`/api/preview/deposits/${depositId}/exit`);
    return data;
}

/** Backend-computed live repay quote for a loan. */
export async function fetchRepayPreview(depositId) {
    const { data } = await api.get(`/api/preview/loans/${depositId}/repay`);
    return data;
}

/** Backend-computed liquidation projection for a loan. */
export async function fetchLiquidationPreview(depositId) {
    const { data } = await api.get(`/api/preview/loans/${depositId}/liquidation`);
    return data;
}

/**
 * Backend-computed "resulting LTV" projection for a candidate borrow
 * `amount` against a deposit. Powers the Lending + Turbo modals'
 * post-action LTV preview.
 */
export async function fetchBorrowPreview(depositId, amount) {
    const { data } = await api.get(
        `/api/preview/loans/${depositId}/borrow?amount=${encodeURIComponent(String(amount))}`
    );
    return data;
}

/** Backend-computed deposit projection (routing + maturity + interest). */
export async function fetchDepositPreview(principal) {
    const { data } = await api.get(
        `/api/preview/deposit?principal=${encodeURIComponent(String(principal))}`
    );
    return data;
}

/** Returns the loan attached to a deposit (or null if none). */
export async function fetchLoanByDeposit(depositId) {
    const { data } = await api.get(`/api/lending/loans/${depositId}`);
    return data;
}

/**
 * Returns a loan's full activity timeline for a deposit — every draw,
 * repayment, self-paying-netting and liquidation — from the indexer's
 * event log. Multiple draws against one deposit each show as a row.
 */
export async function fetchLoanActivity(depositId) {
    const { data } = await api.get(`/api/lending/loans/${depositId}/activity`);
    return data;
}

/** Returns loans for a borrower. Pass `includeClosed: true` to also
 *  include historical (repaid + liquidated) loans for the Closed tab. */
export async function fetchLoansByOwner(owner, { includeClosed = false } = {}) {
    const qs = includeClosed ? "&includeClosed=true" : "";
    const { data } = await api.get(`/api/lending/loans?owner=${owner}${qs}`);
    return data;
}

/** Returns aggregate lending-pool health (TVL, total outstanding, average LTV). */
export async function fetchLendingHealth() {
    const { data } = await api.get("/api/lending/health");
    return data;
}

/** Returns USDC peg status (current price, deviation, depegged flag). */
export async function fetchPegStatus() {
    const { data } = await api.get("/api/monitoring/peg");
    return data;
}

/**
 * Returns the current CHAIN time (latest block timestamp). The single
 * authority for every time-sensitive figure across the stack — the
 * frontend chain clock (utils/chainClock.js) mirrors this so display,
 * calculation and the backend all key off the exact same clock.
 */
export async function fetchChainTime() {
    const { data } = await api.get("/api/chain/time");
    return data;
}

/** Returns active alerts (open + unresolved). Pass `includeResolved: true`
 *  to also include past resolved alerts. */
export async function fetchAlerts({ includeResolved = false } = {}) {
    const qs = includeResolved ? "?includeResolved=true" : "";
    const { data } = await api.get(`/api/monitoring/alerts${qs}`);
    return data;
}

/** Marks an open alert as resolved. Idempotent. */
export async function resolveAlert(alertId) {
    const { data } = await api.post(`/api/monitoring/alerts/${alertId}/resolve`);
    return data;
}

/* ------------------------------------------------------------------ */
/*  Protocol health & solvency                                         */
/* ------------------------------------------------------------------ */

/**
 * Single-call protocol health view — Treasury reserves, SafeVault, loan
 * exposure, solvency-floor coverage, peg flag, dormant/abandoned counts
 * and open critical/warning alert totals. Drives the Dashboard "system
 * status" tile and the global PausedDepositsBanner.
 */
export async function fetchProtocolHealth() {
    const { data } = await api.get("/api/solvency/health");
    return data;
}

/** Returns §11.7 Interest Reserve coverage snapshot (forward obligation
 *  vs reserve, coverage ratio in bps). `refresh=true` forces a fresh
 *  sweep before responding. */
export async function fetchInterestCoverage({ refresh = false } = {}) {
    const { data } = await api.get(`/api/solvency/interest-coverage${refresh ? "?refresh=true" : ""}`);
    return data;
}

/** Lists deposits in their post-maturity dormant / abandoned phases.
 *  Pass `phase: "abandoned"` (or "dormant" / "matured") to filter. */
export async function fetchDormantReport({ phase } = {}) {
    const qs = phase ? `?phase=${encodeURIComponent(phase)}` : "";
    const { data } = await api.get(`/api/solvency/dormant${qs}`);
    return data;
}

/* ------------------------------------------------------------------ */
/*  M3 — Turbo & AutoCompound dashboard reads                          */
/* ------------------------------------------------------------------ */

/** Live Turbo module status (paused flag, wired contracts, LTV caps). */
export async function fetchTurboStatus() {
    const { data } = await api.get("/api/leverage/turbo/status");
    return data;
}

/** Recent TurboExecuted events for the dashboard activity strip. */
export async function fetchTurboHistory({ limit = 50 } = {}) {
    const { data } = await api.get(`/api/leverage/turbo/history?limit=${limit}`);
    return data;
}

/** Pure read-only preview of executing Turbo on a specific deposit. */
export async function fetchTurboPreview(depositId, { borrow } = {}) {
    const qs = borrow ? `?borrow=${borrow.toString()}` : "";
    const { data } = await api.get(`/api/leverage/turbo/preview/${depositId}${qs}`);
    return data;
}

/* ------------------------------------------------------------------ */
/*  M4 — Consolidated Yield Reinvestment                               */
/* ------------------------------------------------------------------ */

/** Returns every Consolidated Reinvestment Pool owned by `address`. */
export async function fetchReinvestmentPoolsByOwner(owner) {
    const { data } = await api.get(`/api/reinvestment/pools/by-owner/${owner}`);
    return data;
}

/**
 * Returns a single pool enriched with a live per-member settlement
 * preview (days compounded, projected compounded balance, pending
 * bonus) and the pool's aggregate pending Treasury liability.
 */
export async function fetchReinvestmentPool(poolId) {
    const { data } = await api.get(`/api/reinvestment/pools/${poolId}`);
    return data;
}

/** Returns materialized-settlement history; optional `poolId` filter. */
export async function fetchReinvestmentSettlements({ poolId, limit, skip } = {}) {
    const params = new URLSearchParams();
    if (poolId != null) params.set("poolId", String(poolId));
    if (limit != null) params.set("limit", String(limit));
    if (skip != null) params.set("skip", String(skip));
    const qs = params.toString();
    const { data } = await api.get(`/api/reinvestment/settlements${qs ? `?${qs}` : ""}`);
    return data;
}

/** Returns the deferred-settlement reconciliation report (chain vs index). */
export async function fetchReinvestmentReconciliation() {
    const { data } = await api.get("/api/reinvestment/reconciliation");
    return data;
}

/**
 * Force-syncs the backend's snapshot of a pool against canonical chain
 * state. The frontend pings this after a successful write (createPool,
 * attach, detach, sweep) so the user sees the post-tx state immediately
 * even if the WS handler missed the event.
 */
export async function syncReinvestmentPool(poolId) {
    const { data } = await api.post(`/api/reinvestment/pools/${poolId}/sync`);
    return data;
}

/**
 * Returns the canonical jurisdiction record for a given two-letter ISO
 * country code (or the caller's edge-resolved country if `code` is null).
 * Result shape: { code, status, restricted, disclaimer }.
 */
export async function lookupJurisdiction(code) {
    const qs = code ? `?code=${encodeURIComponent(String(code).toUpperCase())}` : "";
    const { data } = await api.get(`/api/jurisdiction/lookup${qs}`);
    return data;
}

/** Reads the on-record jurisdiction acknowledgement for a wallet. */
export async function fetchJurisdictionAck(address) {
    const { data } = await api.get(`/api/jurisdiction/users/${address}/acknowledge`);
    return data;
}

/**
 * Records the user's jurisdictional + risk-disclosure acknowledgement
 * against their wallet address. Returns 403 + reason if the chosen code
 * is on the restricted list. Idempotent.
 */
export async function ackJurisdiction(address, { jurisdictionCode, riskDisclosureAcknowledged }) {
    const { data } = await api.post(`/api/jurisdiction/users/${address}/acknowledge`, {
        jurisdictionCode,
        riskDisclosureAcknowledged
    });
    return data;
}

export default api;
